import React from 'react';
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddCommentIcon from '@mui/icons-material/AddComment';
import TagIcon from '@mui/icons-material/Tag';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DescriptionIcon from '@mui/icons-material/Description';

export default function Sidebar({
  sessionId,
  savedSessions = [],
  onSessionChange,
  onNewSession,
  onRemoveSession,
  onFileUpload,
  isUploadingFile,
  acceptedFileTypes = [],
  maxFileSizeMB = 10,
  documents = [],
}) {
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await onFileUpload?.(file);
    } finally {
      e.target.value = '';
    }
  };

  const acceptValue = acceptedFileTypes.map((ext) => `.${ext}`).join(',');
  const docsLabel = acceptedFileTypes.length > 0 ? acceptedFileTypes.map((t) => `.${t}`).join(', ') : 'Any';

  return (
    <Paper
      variant="outlined"
      sx={{
        width: 280,
        bgcolor: '#f9fafb',
        borderRight: '1px solid',
        borderColor: 'divider',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionIcon sx={{ fontSize: 16, color: 'primary.main' }} />
          Data Ingestion
        </Typography>
      </Box>

      <Box sx={{ p: 2, flex: 1, overflowY: 'auto' }}>
        <Box>
          <input
            id="xtram-file-upload"
            type="file"
            onChange={handleFileUpload}
            disabled={isUploadingFile}
            accept={acceptValue}
            style={{ display: 'none' }}
          />
          <label htmlFor="xtram-file-upload">
            <Button
              component="span"
              variant="outlined"
              fullWidth
              disabled={isUploadingFile}
              startIcon={<CloudUploadIcon />}
              sx={{ textTransform: 'none', py: 1.5 }}
            >
              {isUploadingFile ? 'Uploading…' : 'Click to upload'}
            </Button>
          </label>
          <Typography sx={{ mt: 1, fontSize: 11, color: 'text.disabled', textAlign: 'center' }}>
            Max {maxFileSizeMB} MB • {docsLabel}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography sx={{ fontSize: 11, fontWeight: 800, color: 'text.disabled', letterSpacing: 0.6, mb: 1 }}>
          SESSION
        </Typography>

        <Typography sx={{ fontSize: 10, color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <TagIcon sx={{ fontSize: 14 }} /> Current Session ID
        </Typography>
        <TextField
          value={sessionId || ''}
          onChange={(e) => onSessionChange?.(e.target.value)}
          size="small"
          fullWidth
        />

        <Button
          onClick={onNewSession}
          variant="contained"
          fullWidth
          startIcon={<AddCommentIcon />}
          sx={{ mt: 1.25, textTransform: 'none' }}
        >
          Start New Chat
        </Button>

        {savedSessions?.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.disabled', letterSpacing: 0.6, mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HistoryIcon sx={{ fontSize: 14 }} /> RECENT HISTORY
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {savedSessions.map((sid) => {
                const active = sid === sessionId;
                return (
                  <Box
                    key={sid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: active ? '#dbeafe' : 'transparent',
                      '&:hover': { bgcolor: active ? '#bfdbfe' : '#f3f4f6' },
                    }}
                    onClick={() => onSessionChange?.(sid)}
                    title={sid}
                  >
                    <Typography sx={{ fontSize: 12, flex: 1 }} noWrap>
                      {sid.length > 18 ? `${sid.slice(0, 15)}…` : sid}
                    </Typography>
                    <Tooltip title="Remove from history">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemoveSession?.(sid);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                );
              })}
            </Box>
          </Box>
        ) : null}

        {documents?.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, color: 'text.disabled', letterSpacing: 0.6, mb: 1 }}>
              SESSION DOCUMENTS
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {documents.map((doc) => (
                <Paper key={doc.document_id} variant="outlined" sx={{ p: 1 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700 }} noWrap>
                    {doc.original_filename || doc.stored_filename}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: 'text.disabled', mt: 0.25 }}>
                    Chunks: {doc.chunk_count || 0} • Chars: {doc.extracted_chars || 0}
                  </Typography>
                </Paper>
              ))}
            </Box>
          </Box>
        ) : null}
      </Box>

      <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f9fafb' }}>
        <Typography sx={{ fontSize: 10, color: 'text.disabled', textAlign: 'center' }}>
          Supported formats: {docsLabel}
        </Typography>
      </Box>
    </Paper>
  );
}

